import { action, computed, makeObservable, onBecomeObserved } from "mobx"

import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import GlobalProjectsCollection from "@src/app/ProjectsCollection"
import {
  ILoadTransactionsRequest,
  Transaction,
} from "@src/app/transactionsStore/TransactionsCollection/types"
import uiStore from "@src/app/uiStore"
import { statusOptions, typeOptions } from "@src/pages/reports/constants"
import { TransactionsDepositsCollectionFiltersType } from "@src/pages/transactionsDeposits/classes/TransactionsDepositsCollection"
import { catchErrors, merchantApi } from "@src/shared/api"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { wrapSingleton } from "@src/shared/classes/utils/wrapSingleton"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"

export class TransactionsCollection extends loading(
  ["fetch"],
  CollectionWithPagination<Transaction>,
) {
  private _currencies = GlobalCurrenciesCollection()
  private _projects = GlobalProjectsCollection()

  constructor(private ui = uiStore()) {
    super()

    makeObservable(this, {
      currencies: computed,
      fConf: computed,
      fetch: action.bound,
      filter: computed,
      projects: computed,
    })

    onBecomeObserved(this, "isLoading", this.load)
  }

  async fetch(req: ILoadTransactionsRequest = {}) {
    try {
      const {
        data: { data },
      } = await merchantApi.getTransactions(
        req.projectId,
        req.status as any,
        req.resource,
        req.externalId,
        req.date?.dateFrom,
        req.date?.dateTo,
        req.currency?.symbol,
        req.currency?.network,
        req.order,
        req.orderBy,
        this.calcOffset(req.page),
        20,
      )

      if (data) {
        const { pagination, transactions } = data as any

        return {
          data: transactions,
          pagination,
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }

    return null
  }

  get currencies() {
    return this._currencies.selectOptions
  }

  get fConf() {
    return [
      CollectionFilters.createField("date").date({ col: 8, label: "Date" }),
      CollectionFilters.createField("resource").select(
        typeOptions.filter((f) =>
          this.ui.isTransferioTheme ? f.value !== "convert" : true,
        ),
        {
          col: 4,
          label: "Select Type",
        },
      ),
      CollectionFilters.createField("currency").select(this.currencies, {
        col: 4,
        label: "Select currency",
        prepareValue: (value) => CollectionFilters.prepares(value).currencies(),
      }),
      CollectionFilters.createField("projectId").select(this.projects, {
        col: 4,
        label: "Select project",
      }),
      CollectionFilters.createField("status").select(statusOptions, {
        col: 4,
        label: "Select status",
      }),
    ]
  }

  get filter() {
    return new CollectionFilters<TransactionsDepositsCollectionFiltersType>(
      this.fConf,
      this.load,
    )
  }

  get projects() {
    return this._projects.selectOptions
  }
}

export default wrapSingleton(TransactionsCollection)
